import { NodeType, NodeDataTypeMap } from '../base';
import type { TreeNode } from '../node';
import { ActivityCategoryDto, ActivityTypeDto, PrivilegeEnum } from '@/api/ApiClient';

/**
 * Returns the privilege required for update/delete/submit/reset on an entity.
 * Must match backend auth handler logic per entity type.
 *
 * Use this when you have the node type and entity data (e.g. in detail components).
 * isAuthorized is null-safe: passing null/undefined for entity will deny access when the branch needs it.
 */
export function getPrivilegeForEntity<T extends NodeType>(
  nodeType: T,
  entity: NodeDataTypeMap[T] | null | undefined
): PrivilegeEnum | null {
  switch (nodeType) {
    case 'sample':
    case 'sampleAggregate':
    case 'sampleInPackage':
    case 'sampleSnapshot':
      return PrivilegeEnum.Sample;

    case 'sampleActivity':
    case 'sampleActivityAggregate':
    case 'sampleActivityInPackage':
    case 'sampleActivitySnapshot': {
      const activity = entity as NodeDataTypeMap['sampleActivity'] | null | undefined;
      const { category, type } = activity ?? {};
      if (!category) return null;
      switch (category) {
        case ActivityCategoryDto.Characterization:
          return PrivilegeEnum.CharacterizationActivity;
        case ActivityCategoryDto.Inventory:
          return PrivilegeEnum.Sample;
        case ActivityCategoryDto.Storage:
        case ActivityCategoryDto.Trash:
          return PrivilegeEnum.StorageActivity;
        case ActivityCategoryDto.Transfer:
          return PrivilegeEnum.TransferActivity;
        case ActivityCategoryDto.Processing:
          return type === ActivityTypeDto.Dicing ? PrivilegeEnum.SplitActivity : PrivilegeEnum.ProcessingActivity;
        default:
          return null;
      }
    }

    case 'project':
    case 'projectInData': {
      const project = entity as NodeDataTypeMap['project'] | null | undefined;
      return project?.type?.isInternal ? PrivilegeEnum.InternalProject : PrivilegeEnum.UserProject;
    }

    case 'projectActivity': {
      const activity = entity as NodeDataTypeMap['projectActivity'] | null | undefined;
      switch (activity?.type) {
        case ActivityTypeDto.Publication:
        case ActivityTypeDto.Presentation:
          return PrivilegeEnum.Publication;
        case ActivityTypeDto.Award:
        case ActivityTypeDto.Proposal:
          return PrivilegeEnum.Proposal;
        case ActivityTypeDto.Decision:
          return PrivilegeEnum.ProjectDecision;
        case ActivityTypeDto.Document:
          return PrivilegeEnum.ProjectDocument;
        case ActivityTypeDto.Report:
          return PrivilegeEnum.Report;
        default:
          return PrivilegeEnum.UserProject;
      }
    }

    case 'theoryActivity':
    case 'theoryActivityInPackage':
      return PrivilegeEnum.TheoryActivity;

    case 'shipping':
      return PrivilegeEnum.Shipping;

    case 'communication':
      return PrivilegeEnum.Communication;

    case 'dataPackage':
    case 'simulationDataset':
      return PrivilegeEnum.DataPackage;

    case 'publication':
    case 'publicationInData':
      return PrivilegeEnum.Publication;

    default:
      return null;
  }
}

/**
 * Returns the privilege required for update/delete/submit/reset on a node.
 * Convenience wrapper when you have a TreeNode and getTypedData (e.g. in action registry).
 * isAuthorized is null-safe: passing null/undefined will deny access.
 */
export function getPrivilegeForNode<T extends NodeType>(
  node: TreeNode<T>,
  getTypedData: (n: TreeNode<T>) => NodeDataTypeMap[T] | null
): PrivilegeEnum | null {
  return getPrivilegeForEntity(node.type, getTypedData(node));
}

/**
 * Returns the privilege for a note based on ownership.
 * OwnNote for own notes, Note for others' notes. Matches backend NoteAuthHandler.
 */
export function getPrivilegeForNote(
  note: { lastChangedByUser?: string } | null,
  currentUserId: string | undefined
): PrivilegeEnum {
  if (!note || !currentUserId) return PrivilegeEnum.Note;
  return note.lastChangedByUser === currentUserId ? PrivilegeEnum.OwnNote : PrivilegeEnum.Note;
}

/**
 * Returns the privilege for a file based on ownership.
 * OwnFile for own files, File for others' files. Matches backend FileAuthHandler.
 */
export function getPrivilegeForFile(
  file: { lastChangedByUser?: string } | null,
  currentUserId: string | undefined
): PrivilegeEnum {
  if (!file || !currentUserId) return PrivilegeEnum.File;
  return file.lastChangedByUser === currentUserId ? PrivilegeEnum.OwnFile : PrivilegeEnum.File;
}
